import { Fragment, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApiMutation } from '../../api/hooks.js';
import { Banner, Button, Field, Icon, Modal, Textarea } from '../../ui/index.js';
import {
  describeTakeOffSaleError,
  postParentTakeOffSale,
  postParentsBulkTakeOffSale,
  type ParentsBulkTakeOffSaleResponse,
} from './parentSaleApi.js';

export interface TakeOffSaleTarget {
  readonly id: string;
  readonly accountHandle: string;
}

export interface TakeOffSaleDialogProps {
  readonly providerId: string;
  readonly open: boolean;
  /** 'single' → the row menu (one parent); 'bulk' → the selected rows. */
  readonly mode: 'single' | 'bulk';
  readonly targets: readonly TakeOffSaleTarget[];
  readonly onClose: () => void;
  /** Called after the server answered 200 (the list should refresh). */
  readonly onDone: (disabledIds: readonly string[]) => void;
}

const MIN_REASON = 4;
const HANDLES_SHOWN = 8;

/**
 * 2026-09-23 (D-239) — confirm dialog for "Satıştan çek" (alias-model parents
 * the server serves, see supportsOperatorOffSale). The operator must type a
 * reason (≥4 chars, written to the audit log). The dialog states what the
 * server does; it enforces nothing itself — the in-flight skip, the
 * already-disabled skip, the audit row and the guard that keeps renewal
 * actions from re-activating the parent are all server-side.
 */
export function TakeOffSaleDialog({
  providerId,
  open,
  mode,
  targets,
  onClose,
  onDone,
}: TakeOffSaleDialogProps): JSX.Element | null {
  if (!open || targets.length === 0) return null;
  const title =
    mode === 'single'
      ? <>Satıştan çek — {handleWithBreak(targets[0]!.accountHandle)}</>
      : `Seçilenleri satıştan çek (${targets.length})`;
  // Portal to <body>: the row variant is opened from inside a table cell, whose
  // text-align / white-space would otherwise leak into the dialog.
  return createPortal(
    <Modal open={open} onClose={onClose} title={title} width={560} testId="take-off-sale-dialog">
      <Body providerId={providerId} mode={mode} targets={targets} onClose={onClose} onDone={onDone} />
    </Modal>,
    document.body,
  );
}

/**
 * The handle with soft line-break opportunities before "@" and inside the
 * local part (after ".", "-", "_"), so a long address in the dialog title
 * wraps at a word boundary rather than at an arbitrary letter. Text content
 * (and the accessible name) unchanged.
 */
function handleWithBreak(handle: string): JSX.Element {
  const at = handle.lastIndexOf('@');
  if (at <= 0) return <>{handle}</>;
  // Same hints as the parent cards (ParentMailsTable HandleText): after each
  // ".", "-" or "_" of the local part, and before "@domain".
  const local = handle.slice(0, at).split(/(?<=[.\-_])/).filter((p) => p !== '');
  return (
    <>
      {local.map((part, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {part}
        </Fragment>
      ))}
      <wbr />
      {handle.slice(at)}
    </>
  );
}

function Body({
  providerId,
  mode,
  targets,
  onClose,
  onDone,
}: Omit<TakeOffSaleDialogProps, 'open'>): JSX.Element {
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<ParentsBulkTakeOffSaleResponse | null>(null);
  const single = useApiMutation((o, input: { accountId: string; reason: string }) =>
    postParentTakeOffSale(o, providerId, input.accountId, input.reason),
  );
  const bulk = useApiMutation((o, input: { accountIds: readonly string[]; reason: string }) =>
    postParentsBulkTakeOffSale(o, providerId, input.accountIds, input.reason),
  );
  const running = single.state.kind === 'running' || bulk.state.kind === 'running';
  const trimmed = reason.trim();
  const errorState = mode === 'single' ? single.state : bulk.state;
  const handleOf = new Map(targets.map((t) => [t.id, t.accountHandle] as const));
  const names = (ids: readonly string[]): string =>
    ids.map((id) => handleOf.get(id) ?? id).join(', ');

  async function submit(): Promise<void> {
    if (trimmed.length < MIN_REASON || running) return;
    if (mode === 'single') {
      const r = await single.run({ accountId: targets[0]!.id, reason: trimmed });
      if (r.kind === 'ok') {
        onDone([targets[0]!.id]);
        onClose();
      }
      return;
    }
    const r = await bulk.run({ accountIds: targets.map((t) => t.id), reason: trimmed });
    if (r.kind === 'ok') {
      setResult(r.data);
      onDone(r.data.disabledIds);
    }
  }

  if (result) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="take-off-sale-result">
        <Banner tone={result.disabled === result.requested ? 'success' : 'warn'} title="Sunucu yanıtı">
          <div>
            Satıştan çekildi: <strong>{result.disabled}</strong> / {result.requested}
          </div>
          {result.skippedInFlight > 0 ? (
            <div data-testid="take-off-sale-skipped-inflight">
              Aktif siparişi olduğu için atlandı ({result.skippedInFlight}): {names(result.skippedInFlightIds)}
            </div>
          ) : null}
          {result.skippedAlreadyDisabled > 0 ? (
            <div>
              Zaten devre dışı ({result.skippedAlreadyDisabled}): {names(result.alreadyDisabledIds)}
            </div>
          ) : null}
          {result.notFound > 0 ? (
            <div>
              Bulunamadı ({result.notFound}): {names(result.notFoundIds)}
            </div>
          ) : null}
        </Banner>
        <div className="mcp-form-actions mcp-sheet-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" onClick={onClose} data-testid="take-off-sale-close">
            Kapat
          </Button>
        </div>
      </div>
    );
  }

  const shown = targets.slice(0, HANDLES_SHOWN);
  return (
    <form
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <ul className="t-small" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <li>
          Parent <strong>devre dışı</strong> olur (<em>Satıştan çekildi (operatör)</em>); aliasları yeni siparişe
          verilmez.
        </li>
        <li>Aliaslara dokunulmaz. Aktif siparişi olan parent atlanır; o müşterinin maili gelmeye devam eder.</li>
        <li>
          {providerId === 'mailcom'
            ? 'Geri almak için satırdaki “Yeniden etkinleştir”: parent aktif olur ve kalan aliasları hemen satışa '
              + 'döner; oturum kendiliğinden doğrulanmaz, gerekirse “Oturum yenile”.'
            : 'Geri almak için satırdaki “Yeniden etkinleştir”: parent aktif olur, oturumu yeniden doğrulanır.'}
        </li>
        <li>İşlem nedeniyle birlikte denetim kaydına yazılır.</li>
      </ul>
      <div className="t-mono t-small mcp-tos-targets" data-testid="take-off-sale-targets">
        {/* Each handle is one unit: the list wraps between addresses, and
            inside one only when it is wider than the whole line. */}
        {shown.map((t, i) => (
          <span key={t.id}>
            {i > 0 ? ' ' : null}
            <span className="mcp-tos-handle">
              {t.accountHandle}
              {i < shown.length - 1 ? ',' : null}
            </span>
          </span>
        ))}
        {targets.length > shown.length ? ` +${targets.length - shown.length} daha` : ''}
      </div>
      <Field
        label="Neden (en az 4 karakter, denetim kaydına yazılır)"
        htmlFor="take-off-sale-reason"
        error={
          errorState.kind === 'error'
            ? `Satıştan çekme başarısız: ${describeTakeOffSaleError(errorState.reason)}`
            : undefined
        }
      >
        <Textarea
          id="take-off-sale-reason"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={200}
          data-testid="take-off-sale-reason"
        />
      </Field>
      <div className="mcp-form-actions mcp-sheet-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button type="button" variant="ghost" onClick={onClose} disabled={running}>
          Vazgeç
        </Button>
        <Button
          type="submit"
          variant="danger"
          loading={running}
          disabled={trimmed.length < MIN_REASON}
          icon={<Icon.Cancel size={14} />}
          data-testid="take-off-sale-confirm"
        >
          Satıştan çek
        </Button>
      </div>
    </form>
  );
}
